import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const SKILLS = ["acrobatics", "arcana", "athletics", "crafting", "deception", "diplomacy", "intimidation", "medicine", "nature", "occultism", "performance", "religion", "society", "stealth", "survival", "thievery"];
export const EMPTY_EFFECT = { message: "", damage_dice: "", damage_type: "", apply_condition_slugs: [], push_feet: "", push_direction: "__none" };
const PUSH_DIRECTIONS = ["N", "S", "E", "W", "NE", "NW", "SE", "SW"];

const hydrateEffect = (effect) => ({
  ...EMPTY_EFFECT,
  ...(effect || {}),
  message: effect?.message || "",
  damage_dice: effect?.damage_dice || "",
  damage_type: effect?.damage_type || "",
  apply_condition_slugs: effect?.apply_condition_slugs || [],
  push_feet: effect?.push_feet != null ? String(effect.push_feet) : "",
  push_direction: effect?.push_direction || "__none",
});

export const cleanEffect = (effect) => ({
  message: (effect.message || "").trim() || null,
  damage_dice: (effect.damage_dice || "").trim() || null,
  damage_type: (effect.damage_type || "").trim() || null,
  apply_condition_slugs: effect.apply_condition_slugs || [],
  push_feet: effect.push_direction !== "__none" && effect.push_feet ? parseInt(effect.push_feet, 10) : null,
  push_direction: effect.push_direction !== "__none" ? effect.push_direction : null,
});

export function EffectEditor({ label, effect, onChange, conditions, testPrefix }) {
  const toggleCondition = (slug) => {
    const has = effect.apply_condition_slugs.includes(slug);
    onChange({ ...effect, apply_condition_slugs: has ? effect.apply_condition_slugs.filter((s) => s !== slug) : [...effect.apply_condition_slugs, slug] });
  };
  return (
    <div className="space-y-1.5 bg-black/20 border border-white/10 rounded-xl p-2.5">
      <p className="text-[10px] uppercase text-[#D4AF37]/70">{label}</p>
      <Textarea data-testid={`${testPrefix}-message-input`} value={effect.message} onChange={(e) => onChange({ ...effect, message: e.target.value })} placeholder="Text popup shown to the player (optional)" className="bg-black/30 border-white/10 text-xs min-h-8" />
      <div className="flex gap-2">
        <Input data-testid={`${testPrefix}-damage-dice-input`} value={effect.damage_dice} onChange={(e) => onChange({ ...effect, damage_dice: e.target.value })} placeholder="Damage e.g. 2d6" className="bg-black/30 border-white/10 text-xs flex-1" />
        <Input data-testid={`${testPrefix}-damage-type-input`} value={effect.damage_type} onChange={(e) => onChange({ ...effect, damage_type: e.target.value })} placeholder="Type e.g. fire" className="bg-black/30 border-white/10 text-xs flex-1" />
      </div>
      <p className="text-[9px] text-[#f1e0c5]/30">Auto-rolled and applied to the player's HP, checked against their sheet's resistances/weaknesses/immunities.</p>
      <div className="flex gap-2">
        <Input data-testid={`${testPrefix}-push-feet-input`} type="number" min={0} value={effect.push_feet} onChange={(e) => onChange({ ...effect, push_feet: e.target.value })} placeholder="Push feet e.g. 10" className="bg-black/30 border-white/10 text-xs flex-1" />
        <Select value={effect.push_direction || "__none"} onValueChange={(v) => onChange({ ...effect, push_direction: v })}>
          <SelectTrigger data-testid={`${testPrefix}-push-direction-select`} className="bg-black/30 border-white/10 text-xs flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">No push</SelectItem>
            {PUSH_DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <p className="text-[9px] text-[#f1e0c5]/30">Shoves the triggering token this many feet in a fixed direction you set here.</p>
      <div>
        <p className="text-[9px] uppercase text-[#f1e0c5]/30 mb-1">Apply conditions</p>
        <div className="flex flex-wrap gap-1">
          {conditions.map((c) => (
            <button
              key={c.slug}
              type="button"
              data-testid={`${testPrefix}-condition-${c.slug}`}
              onClick={() => toggleCondition(c.slug)}
              className={`text-[9px] px-2 py-0.5 rounded-full border ${effect.apply_condition_slugs.includes(c.slug) ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/10 text-[#f1e0c5]/50"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Shared DC/Skill/Outcome/Effects block, reused identically for Side A and Side B on line-shaped
// triggers (a two-sided tripwire crossed the opposite way can fire a totally independent trap).
function SideConfig({ testPrefix, dc, setDc, skill, setSkill, outcomeTrigger, setOutcomeTrigger, successEffect, setSuccessEffect, failEffect, setFailEffect, conditions, sharesOutcomeToken }) {
  return (
    <div className="space-y-2">
      <div className="flex gap-3">
        <div className="flex-1">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">DC</p>
          <Input data-testid={`${testPrefix}-dc-input`} type="number" value={dc} onChange={(e) => setDc(e.target.value)} className="bg-black/30 border-white/10" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Skill (optional)</p>
          <Select value={skill} onValueChange={setSkill}>
            <SelectTrigger data-testid={`${testPrefix}-skill-select`} className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Flat d20</SelectItem>
              {SKILLS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Select value={outcomeTrigger} onValueChange={setOutcomeTrigger}>
        <SelectTrigger data-testid={`${testPrefix}-outcome-trigger-select`} className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No token spawn — just the roll</SelectItem>
          <SelectItem value="success">On Success — reveal a token</SelectItem>
          <SelectItem value="fail">On Fail — spring a token</SelectItem>
          <SelectItem value="both">Always — a token appears either way</SelectItem>
        </SelectContent>
      </Select>
      {outcomeTrigger !== "none" && sharesOutcomeToken && (
        <p className="text-[9px] text-[#f1e0c5]/30">Spawns the same token configured above (What appears / Image / Vanish timer).</p>
      )}
      <EffectEditor label="On Success" effect={successEffect} onChange={setSuccessEffect} conditions={conditions} testPrefix={`${testPrefix}-success-effect`} />
      <EffectEditor label="On Fail" effect={failEffect} onChange={setFailEffect} conditions={conditions} testPrefix={`${testPrefix}-fail-effect`} />
    </div>
  );
}

export default function ObstacleCreateDialog({ open, onOpenChange, gameId, sceneId, pos, points, existing, onSaved, onDeleted }) {
  const shape = existing?.shape || (points ? "line" : "circle");
  const [label, setLabel] = useState("Obstacle");
  const [dc, setDc] = useState(15);
  const [skill, setSkill] = useState("__none");
  const [outcomeTrigger, setOutcomeTrigger] = useState("none");
  const [outcomeName, setOutcomeName] = useState("");
  const [outcomeImage, setOutcomeImage] = useState("");
  const [effectDuration, setEffectDuration] = useState("");
  const [successEffect, setSuccessEffect] = useState(EMPTY_EFFECT);
  const [failEffect, setFailEffect] = useState(EMPTY_EFFECT);
  const [conditions, setConditions] = useState([]);
  const [triggerLimit, setTriggerLimit] = useState("");
  const [globalUnlock, setGlobalUnlock] = useState(false);
  const [sideBEnabled, setSideBEnabled] = useState(false);
  const [sideBDc, setSideBDc] = useState(15);
  const [sideBSkill, setSideBSkill] = useState("__none");
  const [sideBLabel, setSideBLabel] = useState("Obstacle");
  const [sideBOutcomeTrigger, setSideBOutcomeTrigger] = useState("none");
  const [sideBSuccessEffect, setSideBSuccessEffect] = useState(EMPTY_EFFECT);
  const [sideBFailEffect, setSideBFailEffect] = useState(EMPTY_EFFECT);

  useEffect(() => {
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
  }, []);

  useEffect(() => {
    if (existing) {
      setLabel(existing.label || "Obstacle");
      setDc(existing.dc ?? 15);
      setSkill(existing.skill || "__none");
      setOutcomeTrigger(existing.outcome_trigger || "none");
      setOutcomeName(existing.outcome_token_name || "");
      setOutcomeImage(existing.outcome_token_image_url || "");
      setEffectDuration(existing.effect_duration_seconds != null ? String(existing.effect_duration_seconds) : "");
      setSuccessEffect(hydrateEffect(existing.success_effect));
      setFailEffect(hydrateEffect(existing.fail_effect));
      setTriggerLimit(existing.max_triggers_per_member != null ? String(existing.max_triggers_per_member) : "");
      setGlobalUnlock(!!existing.disable_globally_after_limit);
      setSideBEnabled(!!existing.side_b_enabled);
      setSideBDc(existing.side_b_dc ?? 15);
      setSideBSkill(existing.side_b_skill || "__none");
      setSideBLabel(existing.side_b_label || "Obstacle");
      setSideBOutcomeTrigger(existing.side_b_outcome_trigger || "none");
      setSideBSuccessEffect(hydrateEffect(existing.side_b_success_effect));
      setSideBFailEffect(hydrateEffect(existing.side_b_fail_effect));
    } else {
      setLabel("Obstacle");
      setDc(15);
      setSkill("__none");
      setOutcomeTrigger("none");
      setOutcomeName("");
      setOutcomeImage("");
      setEffectDuration("");
      setSuccessEffect(EMPTY_EFFECT);
      setFailEffect(EMPTY_EFFECT);
      setTriggerLimit("");
      setGlobalUnlock(false);
      setSideBEnabled(false);
      setSideBDc(15);
      setSideBSkill("__none");
      setSideBLabel("Obstacle");
      setSideBOutcomeTrigger("none");
      setSideBSuccessEffect(EMPTY_EFFECT);
      setSideBFailEffect(EMPTY_EFFECT);
    }
  }, [existing, open]);

  const save = async () => {
    const body = {
      label, dc: parseInt(dc, 10) || 0, skill: skill === "__none" ? null : skill,
      outcome_trigger: outcomeTrigger, outcome_token_name: outcomeName.trim() || null, outcome_token_image_url: outcomeImage.trim() || null,
      effect_duration_seconds: outcomeTrigger !== "none" && effectDuration.trim() ? parseInt(effectDuration, 10) : null,
      success_effect: cleanEffect(successEffect),
      fail_effect: cleanEffect(failEffect),
      max_triggers_per_member: triggerLimit.trim() ? Math.max(1, parseInt(triggerLimit, 10) || 1) : null,
      disable_globally_after_limit: triggerLimit.trim() ? globalUnlock : false,
      side_b_enabled: shape === "line" ? sideBEnabled : false,
      side_b_dc: parseInt(sideBDc, 10) || 0,
      side_b_skill: sideBSkill === "__none" ? null : sideBSkill,
      side_b_label: sideBLabel,
      side_b_outcome_trigger: sideBOutcomeTrigger,
      side_b_success_effect: cleanEffect(sideBSuccessEffect),
      side_b_fail_effect: cleanEffect(sideBFailEffect),
    };
    try {
      if (existing) {
        const { data } = await api.patch(`/games/${gameId}/obstacles/${existing.id}`, body);
        onSaved?.(data);
        toast.success("Obstacle updated");
      } else if (shape === "line") {
        const { data } = await api.post(`/games/${gameId}/obstacles`, { scene_id: sceneId, shape: "line", points, x: points[0].x, y: points[0].y, ...body });
        onSaved?.(data);
        toast.success("Invisible trigger line placed");
      } else {
        const { data } = await api.post(`/games/${gameId}/obstacles`, { scene_id: sceneId, x: pos.x, y: pos.y, radius: 40, shape: "circle", ...body });
        onSaved?.(data);
        toast.success("Invisible obstacle placed");
      }
      onOpenChange(false);
    } catch {
      toast.error("Could not save obstacle");
    }
  };

  const remove = async () => {
    if (!existing) return;
    if (!window.confirm(`Delete "${existing.label || "this obstacle"}"? This can't be undone.`)) return;
    await api.delete(`/games/${gameId}/obstacles/${existing.id}`);
    onDeleted?.(existing.id);
    onOpenChange(false);
  };

  const resetTriggers = async () => {
    if (!existing) return;
    await api.patch(`/games/${gameId}/obstacles/${existing.id}`, { reset_triggers: true });
    toast.success("Players can trigger this obstacle again");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm max-h-[85vh] overflow-y-auto" data-testid="obstacle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{existing ? "Edit Obstacle" : shape === "line" ? "New Trigger Line" : "Place Invisible Obstacle"}</DialogTitle>
          <DialogDescription>
            {shape === "line"
              ? "Invisible to players. Crossing this line one direction fires Side A; crossing it back can optionally fire an independent Side B."
              : "Invisible to players. When a player's own token walks into it, they get a DC check prompt."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">{shape === "line" ? "Side A Prompt Label" : "Prompt Label"}</p>
            <Input data-testid="obstacle-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="To climb, roll to beat DC..." className="bg-black/30 border-white/10" />
          </div>
          <SideConfig
            testPrefix="obstacle"
            dc={dc} setDc={setDc} skill={skill} setSkill={setSkill}
            outcomeTrigger={outcomeTrigger} setOutcomeTrigger={setOutcomeTrigger}
            successEffect={successEffect} setSuccessEffect={setSuccessEffect}
            failEffect={failEffect} setFailEffect={setFailEffect}
            conditions={conditions} sharesOutcomeToken={false}
          />
          {outcomeTrigger !== "none" && (
            <div className="space-y-2 border-t border-white/10 pt-2">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">Spawned token (shared by both sides)</p>
              <Input data-testid="obstacle-outcome-name-input" value={outcomeName} onChange={(e) => setOutcomeName(e.target.value)} placeholder="What appears? e.g. Treasure Chest, Goblin Ambush" className="bg-black/30 border-white/10" />
              <Input data-testid="obstacle-outcome-image-input" value={outcomeImage} onChange={(e) => setOutcomeImage(e.target.value)} placeholder="Token image URL (optional)" className="bg-black/30 border-white/10" />
              <Input data-testid="obstacle-effect-duration-input" type="number" min={0} value={effectDuration} onChange={(e) => setEffectDuration(e.target.value)} placeholder="Vanish after (seconds) — blank = stays forever" className="bg-black/30 border-white/10" />
            </div>
          )}

          {shape === "line" && (
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#f1e0c5]/70">Enable Side B (opposite crossing direction)</p>
                <Switch data-testid="obstacle-side-b-toggle" checked={sideBEnabled} onCheckedChange={setSideBEnabled} />
              </div>
              {sideBEnabled && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Side B Prompt Label</p>
                  <Input data-testid="obstacle-side-b-label-input" value={sideBLabel} onChange={(e) => setSideBLabel(e.target.value)} className="bg-black/30 border-white/10" />
                  <SideConfig
                    testPrefix="obstacle-side-b"
                    dc={sideBDc} setDc={setSideBDc} skill={sideBSkill} setSkill={setSideBSkill}
                    outcomeTrigger={sideBOutcomeTrigger} setOutcomeTrigger={setSideBOutcomeTrigger}
                    successEffect={sideBSuccessEffect} setSuccessEffect={setSideBSuccessEffect}
                    failEffect={sideBFailEffect} setFailEffect={setSideBFailEffect}
                    conditions={conditions} sharesOutcomeToken
                  />
                </div>
              )}
            </div>
          )}

          <div className="border-t border-white/10 pt-3 space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40">Trigger Limit</p>
            <div className="flex gap-2 items-center">
              <Input data-testid="obstacle-trigger-limit-input" type="number" min={1} value={triggerLimit} onChange={(e) => setTriggerLimit(e.target.value)} placeholder="Blank = infinite" className="bg-black/30 border-white/10 flex-1" />
              <span className="text-[10px] text-[#f1e0c5]/30 shrink-0">times per player</span>
            </div>
            {triggerLimit.trim() && (
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-[#f1e0c5]/70">Once any player hits the limit, disable for everyone</p>
                <Switch data-testid="obstacle-global-unlock-toggle" checked={globalUnlock} onCheckedChange={setGlobalUnlock} />
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="obstacle-save-button" onClick={save} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]">
              {existing ? "Save" : shape === "line" ? "Place Trigger Line" : "Place Obstacle"}
            </Button>
            {existing && (
              <>
                <Button data-testid="obstacle-reset-button" variant="secondary" className="rounded-full" onClick={resetTriggers}>Reset</Button>
                <Button data-testid="obstacle-delete-button" variant="secondary" className="rounded-full text-[#D90429]" onClick={remove}>Delete</Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
